import { CommunityRelationshipType, CustomListKey, IClient } from "@sp-crm/core";
import { NgBylinePill } from "components/byline/ng-byline-pill";
import { Col, Row } from "components/shared/grid";
import React, { useMemo } from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useCustomList } from "store/selectors/bridge";
import { useNewLayout, useResponsiveMode } from "store/selectors/hooks";
import { Subpage } from ".";
import { ActionBarAssignedTo } from "./action-bar-assigned-to";
import { ActionBarDateComponent } from "./action-bar-date-component";
import { ActionBarMoveCommunity } from "./action-bar-move-community";
import { ActionBarStatus } from "./action-bar-status";
import { InvoiceSummary } from "./invoices/invoice-summary";

interface ShowClientActionBarProps {
    reactivate(): void;
    client: IClient;
    goToTab?: (section: Subpage) => void;
    hasActiveSubpage: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFieldChange: (fieldName: string, newValue: any) => void;
}

export const ShowClientActionBar: React.FC<ShowClientActionBarProps> = props => {
    const { client, reactivate, goToTab, hasActiveSubpage, onFieldChange } = props;
    const newLayout = useNewLayout();
    const responsiveMode = useResponsiveMode();
    const communities = useMemo(() => {
        const clientCommunities = client?.clientCommunities || [];
        return clientCommunities
            .filter(
                cc =>
                    cc.relationship === CommunityRelationshipType.featured ||
                    client.chosenCommunityId === cc.community?.id,
            )
            .map(cc => cc.community);
    }, [client?.clientCommunities, client?.chosenCommunityId]);

    const { getListItemKey } = useCustomList(CustomListKey.ClientStatus);

    const clientStatus = getListItemKey(client.statusListItemId);

    let scheduledClassName = "";

    switch (clientStatus) {
        case "moved":
        case "scheduled":
            scheduledClassName = " action-bar-accented-component";
            break;
    }

    if (newLayout) {
        const shouldShowActionBar =
            responsiveMode >= ResponsiveMode.large || !hasActiveSubpage;
        return (
            <div
                className={
                    shouldShowActionBar
                        ? "opacity-100 max-h-96"
                        : "opacity-0 max-h-0 overflow-hidden"
                }>
                <div className="@container space-y-2 px-4 py-2">
                    <div className="flex flex-wrap gap-2 md:gap-3">
                        <div>
                            <ActionBarStatus client={client} reactivate={reactivate} />
                        </div>
                        {clientStatus === "checkLater" ? (
                            <div>
                                <ActionBarDateComponent
                                    useNewLayout={newLayout}
                                    className="check-back-later-date"
                                    client={client}
                                    datePropertyName="statusCheckLaterDate"
                                    dateVal={client.statusCheckLaterDate}
                                    displayLabel="Check Back Later Date"
                                />
                            </div>
                        ) : null}
                        <div>
                            <ActionBarDateComponent
                                useNewLayout={newLayout}
                                client={client}
                                datePropertyName="moveTimeline"
                                dateVal={client.moveTimeline}
                                displayLabel="Estimated Move Timeline"
                            />
                        </div>
                        <div>
                            <ActionBarMoveCommunity
                                useNewLayout={newLayout}
                                className={scheduledClassName}
                                client={client}
                                communities={communities}
                                goToTab={(section: Subpage) => goToTab(section)}
                            />
                        </div>
                        <div>
                            <ActionBarDateComponent
                                useNewLayout={newLayout}
                                className={scheduledClassName}
                                client={client}
                                datePropertyName="moveTimelineActual"
                                dateVal={client.moveTimelineActual}
                                displayLabel="Move Date"
                            />
                        </div>
                        <InvoiceSummary clientId={client.id} />
                        <div>
                            <ActionBarAssignedTo
                                useNewLayout={newLayout}
                                client={client}
                            />
                        </div>
                        <NgBylinePill
                            entity={client}
                            mode="action-bar"
                            createdAtDateModified={(newDate: Date) =>
                                onFieldChange("dateAddedManualOverride", newDate)
                            }
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="action-bar">
            <Row className={"flex-stacking-columns"}>
                <Col className="widget">
                    <ActionBarStatus client={client} reactivate={reactivate} />
                </Col>
                {clientStatus === "checkLater" ? (
                    <Col className="widget">
                        <ActionBarDateComponent
                            useNewLayout={newLayout}
                            className="check-back-later-date"
                            client={client}
                            datePropertyName="statusCheckLaterDate"
                            dateVal={client.statusCheckLaterDate}
                            displayLabel="Check Back Later Date"
                        />
                    </Col>
                ) : null}
                <Col className="widget">
                    <ActionBarDateComponent
                        useNewLayout={newLayout}
                        client={client}
                        datePropertyName="moveTimeline"
                        dateVal={client.moveTimeline}
                        displayLabel="Estimated Move Timeline"
                    />
                </Col>
                <Col className="widget">
                    <ActionBarMoveCommunity
                        useNewLayout={newLayout}
                        className={scheduledClassName}
                        client={client}
                        communities={communities}
                        goToTab={(section: Subpage) => goToTab(section)}
                    />
                </Col>
                <Col className="widget">
                    <ActionBarDateComponent
                        useNewLayout={newLayout}
                        className={scheduledClassName}
                        client={client}
                        datePropertyName="moveTimelineActual"
                        dateVal={client.moveTimelineActual}
                        displayLabel="Move Date"
                    />
                </Col>
                <InvoiceSummary clientId={client.id} />
                <Col className="widget">
                    <ActionBarAssignedTo useNewLayout={newLayout} client={client} />
                </Col>
                <Col className="widget">
                    <NgBylinePill entity={client} mode="action-bar" className="mb-2" />
                </Col>
            </Row>
        </div>
    );
};
